/**
 * Calculation engine.
 *
 * Pure. No UI, no database, no network, no clock, no randomness. Inputs in,
 * totals out. That is what lets the same code run on the device for live
 * recalculation (FR31) and on the server for the authoritative figure at
 * issue (FR32).
 *
 * ── Money ─────────────────────────────────────────────────────────────────
 * All arithmetic happens in **integer minor units (cents)** and integer tenths
 * of an hour. Nothing is added, multiplied or compared as a floating-point
 * currency value. Values are converted back to numbers only on the way out.
 *
 * This is not fussiness. `0.1 + 0.2 !== 0.3` in every IEEE-754 language, and an
 * estimating package that loses a cent per line loses a customer the first time
 * a total disagrees with a printed copy. A property test in this module
 * (`no money value carries more than two decimal places`) fails if anyone
 * reintroduces float arithmetic here.
 */

import type {
  CalcInput,
  CalcResult,
  EstimateLine,
  LabourBreakdown,
  RateProfile,
  TaxLine,
} from './types.js';

const LABOUR_TYPES = ['body', 'refinish', 'frame', 'mechanical'] as const;

/** Round half away from zero — what a person doing it on paper does. */
function roundHalfUp(n: number): number {
  return n >= 0 ? Math.round(n) : -Math.round(-n);
}

/** Currency units → integer cents. Guards NaN and Infinity at the boundary. */
function toCents(v: unknown): number {
  const n = typeof v === 'number' && Number.isFinite(v) ? v : 0;
  return roundHalfUp(n * 100);
}

/** Hours → integer tenths of an hour, which is the granularity the trade uses. */
function toTenths(v: unknown): number {
  const n = typeof v === 'number' && Number.isFinite(v) ? v : 0;
  return roundHalfUp(n * 10);
}

function centsToMoney(c: number): number {
  return c / 100;
}

function tenthsToHours(t: number): number {
  return t / 10;
}

/** cents-per-hour × tenths-of-an-hour → cents. */
function hourly(rateCents: number, tenths: number): number {
  return roundHalfUp((rateCents * tenths) / 10);
}

/** cents × percent → cents. Percent may be fractional (7.5%). */
function percentOf(baseCents: number, ratePercent: number): number {
  const r = Number.isFinite(ratePercent) ? ratePercent : 0;
  return roundHalfUp((baseCents * r) / 100);
}

function isTaxable(line: EstimateLine, profile: RateProfile): boolean {
  if (line.taxableOverride !== undefined) return line.taxableOverride;
  return profile.taxablePartTypes.includes(line.partType);
}

function emptyLabour(): LabourBreakdown {
  return {
    body: { hours: 0, amount: 0 },
    refinish: { hours: 0, amount: 0 },
    frame: { hours: 0, amount: 0 },
    mechanical: { hours: 0, amount: 0 },
  };
}

/**
 * Calculate an estimate.
 *
 * Deliberate rules, each of which a shop would recognise:
 *  - Suppressed lines contribute nothing at all, but stay on the estimate.
 *  - Part price is multiplied by quantity; labour hours are not.
 *  - Paint materials are charged per refinish hour only.
 *  - Shop materials are charged per body and frame hour. Refinish and mechanical
 *    hours do not attract them.
 *  - Negative part prices are allowed (a credit, a betterment deduction) and are
 *    taxed the same way, so a credit reduces tax as it should.
 *  - Tax components apply to the subtotals they name, in array order. A compound
 *    component applies to the running total including earlier tax.
 */
export function calculate(input: CalcInput): CalcResult {
  const { profile, vehicle } = input;
  const lines = input.lines.filter((l) => !l.suppressed);

  const labour = emptyLabour();
  const tenths: Record<(typeof LABOUR_TYPES)[number], number> = {
    body: 0, refinish: 0, frame: 0, mechanical: 0,
  };

  let partsTaxableC = 0;
  let partsExemptC = 0;

  for (const line of lines) {
    const qty = typeof line.quantity === 'number' && Number.isFinite(line.quantity)
      ? roundHalfUp(line.quantity) : 0;
    const lineC = toCents(line.partPrice) * qty;

    if (isTaxable(line, profile)) partsTaxableC += lineC;
    else partsExemptC += lineC;

    if (line.labourType !== 'none') {
      tenths[line.labourType] += toTenths(line.hours);
    }
  }

  let labourTotalC = 0;
  for (const t of LABOUR_TYPES) {
    const rateC = toCents(profile.labourRates[t]);
    const amountC = hourly(rateC, tenths[t]);
    labour[t] = { hours: tenthsToHours(tenths[t]), amount: centsToMoney(amountC) };
    labourTotalC += amountC;
  }

  const partsTotalC = partsTaxableC + partsExemptC;

  let paintC = hourly(toCents(profile.paintMaterialsPerHour), tenths.refinish);
  const capC = toCents(profile.paintMaterialsCap ?? 0);
  if (capC > 0) paintC = Math.min(paintC, capC);

  const shopC = hourly(toCents(profile.shopMaterialsPerHour), tenths.body + tenths.frame);
  const materialsC = paintC + shopC;

  const subtotalC = partsTotalC + labourTotalC + materialsC;

  const taxes: TaxLine[] = [];
  let taxTotalC = 0;
  for (const comp of profile.taxes) {
    let baseC = 0;
    if (comp.onParts) baseC += partsTaxableC;
    if (comp.onLabour) baseC += labourTotalC;
    if (comp.onMaterials) baseC += materialsC;
    if (comp.compound) baseC += taxTotalC;

    const amountC = percentOf(baseC, comp.rate);
    taxes.push({ name: comp.name, rate: comp.rate, amount: centsToMoney(amountC) });
    taxTotalC += amountC;
  }

  const grandTotalC = subtotalC + taxTotalC;

  let totalLoss: CalcResult['totalLoss'] = null;
  if (vehicle && vehicle.acv > 0 && vehicle.thresholdPercent > 0) {
    const limitC = percentOf(toCents(vehicle.acv), vehicle.thresholdPercent);
    const percent = limitC > 0 ? roundHalfUp((grandTotalC / limitC) * 10000) / 100 : 0;
    totalLoss = {
      limit: centsToMoney(limitC),
      percentOfLimit: percent,
      exceeded: grandTotalC >= limitC,
    };
  }

  return {
    partsTotal: centsToMoney(partsTotalC),
    labour,
    labourTotal: centsToMoney(labourTotalC),
    paintMaterials: centsToMoney(paintC),
    shopMaterials: centsToMoney(shopC),
    materialsTotal: centsToMoney(materialsC),
    subtotal: centsToMoney(subtotalC),
    taxes,
    taxTotal: centsToMoney(taxTotalC),
    grandTotal: centsToMoney(grandTotalC),
    totalLoss,
    includedLineCount: lines.length,
  };
}

export type { CalcInput, CalcResult } from './types.js';
